import React, { useState, useEffect, useCallback } from 'react';

/**
 * PWAInstallPrompt
 * - Shows the "Update Available" banner when a new service-worker build is waiting.
 *
 * Note: the "Install LeadWix / Add to home screen" banners (Android
 * `beforeinstallprompt` + iOS Safari instructions) were intentionally removed.
 */
export default function PWAInstallPrompt() {
  const [updateReady, setUpdateReady]         = useState(false);
  const [swRegistration, setSwRegistration]   = useState(null);

  // Load global SW registration (set by index.js via window)
  useEffect(() => {
    if (window.__SW_REGISTRATION__) setSwRegistration(window.__SW_REGISTRATION__);
    const handleSwReady = (e) => setSwRegistration(e.detail);
    window.addEventListener('sw-update-ready', handleSwReady);
    return () => window.removeEventListener('sw-update-ready', handleSwReady);
  }, []);

  // Update banner: fired from serviceWorkerRegistration.js via custom event
  useEffect(() => {
    const handleUpdate = () => setUpdateReady(true);
    window.addEventListener('sw-update-available', handleUpdate);
    return () => window.removeEventListener('sw-update-available', handleUpdate);
  }, []);

  const handleUpdate = useCallback(() => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setUpdateReady(false);
  }, [swRegistration]);

  // ── Update banner (highest priority) ────────────────────────────────────────
  if (updateReady) {
    return (
      <div style={styles.banner}>
        <div style={styles.inner}>
          <span style={styles.icon}>🔄</span>
          <div style={styles.text}>
            <strong style={styles.title}>Update Available</strong>
            <span style={styles.sub}>A new version of LeadWix is ready.</span>
          </div>
          <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={handleUpdate}>
            Refresh
          </button>
          <button style={styles.closeBtn} onClick={() => setUpdateReady(false)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ── Inline styles (no external CSS dependency) ────────────────────────────────
const styles = {
  banner: {
    position: 'fixed',
    bottom: '1rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(96vw, 480px)',
    zIndex: 9999,
    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.45)',
    color: '#fff',
    padding: '0.875rem 1rem',
    animation: 'slideUp 0.3s ease',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 10,
    flexShrink: 0,
    objectFit: 'cover',
  },
  icon: {
    fontSize: '1.5rem',
    flexShrink: 0,
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sub: {
    fontSize: '0.78rem',
    opacity: 0.88,
    lineHeight: 1.4,
  },
  btn: {
    border: 'none',
    borderRadius: '10px',
    padding: '0.45rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'transform 0.15s',
    whiteSpace: 'nowrap',
  },
  btnPrimary: {
    background: '#fff',
    color: '#7c3aed',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer',
    fontSize: '1rem',
    lineHeight: 1,
    padding: '0.25rem',
    flexShrink: 0,
  },
  shareIcon: {
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: '1rem',
  },
};

// Inject slide-up keyframe once
if (typeof document !== 'undefined' && !document.getElementById('pwa-prompt-style')) {
  const s = document.createElement('style');
  s.id = 'pwa-prompt-style';
  s.textContent = '@keyframes slideUp{from{transform:translateX(-50%) translateY(120%);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}';
  document.head.appendChild(s);
}
